'use client';

import { useTranslations } from 'next-intl';
import { type FC, useState } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { StripeConfirmDialog } from '@/app/components/shared/Pay/components/StripeConfirmDialog';
import { Button } from '@/app/components/ui/button';
import { DialogFooter } from '@/app/components/ui/dialog';
import { usePayment } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { CreditsPurchaseProduct } from '@/app/types';
import { reportEvent } from '@/app/utils';

const PurchaseCreditsFooter: FC<{
  source?: string;
  currentProduct: CreditsPurchaseProduct | null;
}> = ({ source, currentProduct }) => {
  const t = useTranslations();
  const { pay } = usePayment();
  const { payStatus } = useStore((state) => ({
    payStatus: state.payStatus,
  }));

  const [showStripeConfirm, setShowStripeConfirm] = useState(false);

  const handleClickPay = () => {
    if (!currentProduct) return;
    reportEvent('element_click', {
      from_page: source || '',
      element_name: 'pay_credits_button',
      price: currentProduct.price,
    });
    // stripe 有可用券时先弹选券确认框，与订阅 / claw pass 行为一致
    if (currentProduct.pay_channel === 'stripe' && (currentProduct.available_coupons?.length ?? 0) > 0) {
      setShowStripeConfirm(true);
      return;
    }
    pay(currentProduct);
  };

  return (
    <DialogFooter className="w-full items-center px-3 pt-6 sm:flex-col sm:items-center sm:space-x-0 sm:px-4 sm:pt-4">
      <Button
        variant="primary"
        size="dialog"
        className="h-11 w-48 select-none sm:w-full"
        disabled={payStatus === 'loading' || !currentProduct}
        onClick={handleClickPay}
      >
        {payStatus === 'loading' && <Loading className="animate-spin text-base" />}
        <span className="ml-1">{t('popup.dialog.purchaseCredits.pay')}</span>
      </Button>
      {currentProduct && showStripeConfirm && (
        <StripeConfirmDialog
          product={currentProduct}
          open={showStripeConfirm}
          onClose={() => setShowStripeConfirm(false)}
          onConfirm={(couponId) => {
            setShowStripeConfirm(false);
            pay(currentProduct, { couponIdOverride: couponId });
          }}
        />
      )}
    </DialogFooter>
  );
};

export default PurchaseCreditsFooter;
